import { useState, useEffect, useRef, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageSquare,
  Send,
  Search,
  ChevronLeft,
  Loader2,
  Building2,
  Users,
  Paperclip,
  FileText,
  Mic,
  Image as ImageIcon,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { useSocket } from "@/hooks/useSocket";

// ====== Helpers ======
function formatTime(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } else if (days === 1) {
    return "Hier";
  } else if (days < 7) {
    return date.toLocaleDateString("fr-FR", { weekday: "short" });
  }
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function getTypeLabel(type: string) {
  switch (type) {
    case "PROJECT": return "Projet";
    case "TENDER": return "AO";
    case "SUPPORT": return "Support";
    default: return "General";
  }
}

function getTypeBadgeColor(type: string) {
  switch (type) {
    case "PROJECT": return "bg-primary/10 text-primary";
    case "TENDER": return "bg-amber-500/10 text-amber-600";
    case "SUPPORT": return "bg-destructive/10 text-destructive";
    default: return "bg-gray-500/10 text-gray-600";
  }
}

function getAuthorName(author: any): string {
  if (!author) return "Inconnu";
  if (author.firstName || author.lastName) {
    return `${author.firstName || ""} ${author.lastName || ""}`.trim();
  }
  return author.email || "Inconnu";
}

function getInitials(author: any): string {
  if (!author) return "?";
  if (author.firstName && author.lastName) {
    return `${author.firstName[0]}${author.lastName[0]}`.toUpperCase();
  }
  if (author.firstName) return author.firstName[0].toUpperCase();
  if (author.email) return author.email[0].toUpperCase();
  return "?";
}

function getBackendUrl() {
  if (import.meta.env.PROD) {
    return import.meta.env.VITE_BACKEND_URL || "https://api.constrigo.fr";
  }
  return import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";
}

function getAttachmentIcon(fileType: string) {
  if (fileType.startsWith("image/")) return ImageIcon;
  if (fileType.startsWith("audio/")) return Mic;
  return FileText;
}

function AttachmentInline({ att, isStaff }: { att: any; isStaff: boolean }) {
  const url = att.fileUrl.startsWith("http") ? att.fileUrl : `${getBackendUrl()}${att.fileUrl}`;
  const Icon = getAttachmentIcon(att.fileType);

  if (att.fileType.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img
          src={url}
          alt={att.fileName}
          className="max-w-[250px] max-h-[200px] rounded-lg mt-1 hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }

  if (att.fileType.startsWith("audio/")) {
    return (
      <div className="mt-1">
        <audio controls preload="metadata" className="h-8" style={{ minWidth: 180 }}>
          <source src={url} type={att.fileType} />
        </audio>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-2 mt-1 p-2 rounded-lg border text-xs",
        isStaff ? "border-primary-foreground/20" : "border-border"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{att.fileName}</span>
    </a>
  );
}

// ====== Main Component ======
export default function Messages() {
  const {
    connected,
    joinConversation,
    leaveConversation,
    onNewMessage,
    onConversationUpdated,
    onConversationNew,
  } = useSocket();

  // State
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("ALL");
  const [messageInput, setMessageInput] = useState("");
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // tRPC
  const utils = trpc.useUtils();

  const {
    data: conversationsData,
    isLoading: loadingConversations,
    isError: isErrorConversations,
    refetch: refetchConversations,
  } = trpc.messaging.listConversations.useQuery({
    limit: 50,
    type: filterType !== "ALL" ? (filterType as any) : undefined,
  });

  const {
    data: messagesData,
    isLoading: loadingMessages,
    isError: isErrorMessages,
    refetch: refetchMessages,
  } = trpc.messaging.getMessages.useQuery(
    { conversationId: selectedConvId!, limit: 100 },
    { enabled: !!selectedConvId }
  );

  const { data: unreadData } = trpc.messaging.getUnreadCount.useQuery();

  const sendMessageMutation = trpc.messaging.sendMessage.useMutation({
    onSuccess: () => {
      setMessageInput("");
      refetchMessages();
      utils.messaging.listConversations.invalidate();
      inputRef.current?.focus();
    },
    onError: (error: { message: string }) => {
      toast.error("Échec de l'envoi : " + error.message);
    },
  });

  const markAsReadMutation = trpc.messaging.markAsRead.useMutation({
    onSuccess: () => {
      utils.messaging.listConversations.invalidate();
      utils.messaging.getUnreadCount.invalidate();
    },
    onError: () => {
      // Silent — non-critical action
    },
  });

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messagesData?.items]);

  // WebSocket
  useEffect(() => {
    if (!selectedConvId || !connected) return;
    joinConversation(selectedConvId);
    return () => { leaveConversation(selectedConvId); };
  }, [selectedConvId, connected, joinConversation, leaveConversation]);

  useEffect(() => {
    if (!connected) return;
    const unsubMessage = onNewMessage((message: any) => {
      if (message.conversationId === selectedConvId) {
        refetchMessages();
        markAsReadMutation.mutate({ conversationId: selectedConvId! });
      }
      utils.messaging.listConversations.invalidate();
      utils.messaging.getUnreadCount.invalidate();
    });
    const unsubUpdated = onConversationUpdated(() => {
      utils.messaging.listConversations.invalidate();
      utils.messaging.getUnreadCount.invalidate();
    });
    const unsubNew = onConversationNew(() => {
      utils.messaging.listConversations.invalidate();
      utils.messaging.getUnreadCount.invalidate();
    });
    return () => { unsubMessage(); unsubUpdated(); unsubNew(); };
  }, [connected, selectedConvId, onNewMessage, onConversationUpdated, onConversationNew, refetchMessages, utils, markAsReadMutation]);

  const handleSelectConversation = useCallback((convId: string) => {
    setSelectedConvId(convId);
    setMobileShowChat(true);
    markAsReadMutation.mutate({ conversationId: convId });
  }, [markAsReadMutation]);

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedConvId) return;
    sendMessageMutation.mutate({ conversationId: selectedConvId, content: messageInput.trim() });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const conversations: any[] = conversationsData?.items || [];
  const filteredConversations = searchQuery
    ? conversations.filter((c: any) =>
        c.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.company?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  const messages: any[] = messagesData?.items || [];
  const selectedConv = conversations.find((c: any) => c.id === selectedConvId);

  // Build participants display
  const participantNames = selectedConv?.participants
    ?.map((p: any) => {
      if (p.firstName || p.lastName) return `${p.firstName || ""} ${p.lastName || ""}`.trim();
      return p.email || "Inconnu";
    })
    ?.join(", ");

  return (
    <AdminLayout>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Messagerie entreprises</h1>
        <p className="text-sm text-muted-foreground">
          Supervision des conversations internes des entreprises clientes
          {unreadData?.count ? ` — ${unreadData.count} non lue(s)` : ""}
        </p>
      </div>

      <div className="flex h-[calc(100vh-12rem)] rounded-xl border border-border overflow-hidden bg-card">
        {/* ====== LEFT: Conversations ====== */}
        <div className={cn("w-full md:w-80 lg:w-96 border-r border-border flex flex-col", mobileShowChat && "hidden md:flex")}>
          <div className="p-3 border-b border-border space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Rechercher par sujet ou entreprise..." className="pl-9 h-9" aria-label="Rechercher une conversation" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="flex gap-1">
              {["ALL", "GENERAL", "PROJECT", "SUPPORT"].map((type) => (
                <button
                  key={type}
                  onClick={() => { setFilterType(type); setSelectedConvId(null); }}
                  aria-pressed={filterType === type}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                    filterType === type ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  {type === "ALL" ? "Tous" : getTypeLabel(type)}
                </button>
              ))}
            </div>
          </div>

          <ScrollArea className="flex-1">
            {loadingConversations ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : isErrorConversations ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <AlertCircle className="h-8 w-8 text-destructive/40 mb-3" />
                <p className="text-sm text-muted-foreground">Erreur de chargement</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => refetchConversations()}>
                  <RefreshCw className="h-4 w-4 mr-2" />Réessayer
                </Button>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">Aucune conversation</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredConversations.map((conv: any) => (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    aria-label={`Conversation: ${conv.subject} — ${conv.company?.name || ""}`}
                    className={cn(
                      "w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors",
                      selectedConvId === conv.id && "bg-accent",
                      conv.hasUnread && "bg-primary/5"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-medium",
                        conv.hasUnread ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      )}>
                        {conv.company?.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn("text-sm truncate", conv.hasUnread ? "font-semibold" : "font-medium")}>{conv.subject}</span>
                          {conv.lastMessageAt && <span className="text-xs text-muted-foreground shrink-0">{formatTime(conv.lastMessageAt)}</span>}
                        </div>
                        {conv.company && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground truncate">{conv.company.name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", getTypeBadgeColor(conv.type))}>{getTypeLabel(conv.type)}</Badge>
                          {conv.lastMessage && (
                            <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                              {conv.lastMessage.hasAttachment && <Paperclip className="h-3 w-3 shrink-0" />}
                              {conv.lastMessage.authorName || "Employe"}: {conv.lastMessage.content || "Piece jointe"}
                            </p>
                          )}
                        </div>
                      </div>
                      {conv.hasUnread && <div className="h-2.5 w-2.5 rounded-full bg-primary shrink-0 mt-1.5" />}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>

          <div className="px-4 py-2 border-t border-border">
            <div className="flex items-center gap-2">
              <div className={cn("h-2 w-2 rounded-full", connected ? "bg-success" : "bg-destructive")} />
              <span className="text-xs text-muted-foreground">{connected ? "Temps reel actif" : "Deconnecte"}</span>
            </div>
          </div>
        </div>

        {/* ====== RIGHT: Chat ====== */}
        <div className={cn("flex-1 flex flex-col", !mobileShowChat && "hidden md:flex")}>
          {selectedConvId && selectedConv ? (
            <>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileShowChat(false)}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold">
                  {selectedConv.company?.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{selectedConv.subject}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedConv.company && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-3 w-3" />{selectedConv.company.name}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      <Users className="h-3 w-3 inline mr-1" />{participantNames || "Aucun participant"}
                    </span>
                  </div>
                </div>
                <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", getTypeBadgeColor(selectedConv.type))}>
                  {getTypeLabel(selectedConv.type)}
                </Badge>
              </div>

              <ScrollArea className="flex-1 p-4">
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : isErrorMessages ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-8 w-8 text-destructive/40 mb-3" />
                    <p className="text-sm text-muted-foreground">Erreur de chargement des messages</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => refetchMessages()}>
                      <RefreshCw className="h-4 w-4 mr-2" />Réessayer
                    </Button>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <MessageSquare className="h-12 w-12 text-muted-foreground/20 mb-3" />
                    <p className="text-sm text-muted-foreground">Aucun message dans cette conversation</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg: any, index: number) => {
                      const isStaffMsg = msg.authorType === "ADMIN";
                      const authorName = getAuthorName(msg.author);
                      const initials = getInitials(msg.author);
                      const showDate = index === 0 ||
                        new Date(msg.createdAt).toDateString() !== new Date(messages[index - 1].createdAt).toDateString();

                      return (
                        <div key={msg.id}>
                          {showDate && (
                            <div className="flex items-center justify-center my-4">
                              <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                                {new Date(msg.createdAt).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                              </span>
                            </div>
                          )}
                          <div className={cn("flex gap-2", isStaffMsg ? "justify-end" : "justify-start")}>
                            {!isStaffMsg && (
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold mt-1">
                                {initials}
                              </div>
                            )}
                            <div className={cn(
                              "max-w-[75%] rounded-2xl px-4 py-2.5",
                              isStaffMsg ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted rounded-bl-md"
                            )}>
                              <p className={cn("text-xs font-medium mb-1", isStaffMsg ? "text-primary-foreground/70" : "text-primary")}>
                                {authorName} {isStaffMsg && "(Staff)"}
                              </p>
                              {msg.attachments?.length > 0 && (
                                <div className="space-y-1 mb-1">
                                  {msg.attachments.map((att: any) => (
                                    <AttachmentInline key={att.id} att={att} isStaff={isStaffMsg} />
                                  ))}
                                </div>
                              )}
                              {msg.content && <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>}
                              <p className={cn("text-[10px] mt-1", isStaffMsg ? "text-primary-foreground/60" : "text-muted-foreground")}>
                                {new Date(msg.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Staff can intervene in conversations */}
              <div className="border-t border-border p-4">
                <div className="flex items-end gap-2">
                  <Textarea
                    ref={inputRef}
                    placeholder="Intervenir dans la conversation (en tant que staff)..."
                    className="min-h-[44px] max-h-[120px] resize-none"
                    aria-label="Message à envoyer"
                    rows={1}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <Button size="icon" aria-label="Envoyer le message" className="shrink-0 h-11 w-11" onClick={handleSendMessage} disabled={!messageInput.trim() || sendMessageMutation.isPending}>
                    {sendMessageMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Les messages envoyes depuis l'ops sont marques comme Staff</p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/5 mb-4">
                <MessageSquare className="h-10 w-10 text-primary/30" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Supervision messagerie</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Consultez les conversations internes des entreprises clientes.
                Vous pouvez intervenir si necessaire.
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
